import { useEffect, useRef, useState } from 'react'
import { PageMeta } from '../components/seo/PageMeta'
import { GHLCalendar } from '../components/booking/GHLCalendar'
import { SwitchingFaq } from '../components/shared/SwitchingFaq'
import { api } from '../lib/api'
import { pageMeta } from '../lib/seo'
import { contactSchema } from '../lib/schema'

type CaptchaProvider = 'turnstile' | 'recaptcha' | null

export default function Contact() {
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState(false)
  const [smsConsent, setSmsConsent] = useState(false)
  const [captchaProvider, setCaptchaProvider] = useState<CaptchaProvider>(null)
  const [captchaSiteKey, setCaptchaSiteKey] = useState('')
  const turnstileWidgetIdRef = useRef<string | null>(null)

  useEffect(() => {
    api.get<{ captcha_provider: CaptchaProvider; captcha_site_key: string }>('/api/contact/config').then(({ data }) => {
      setCaptchaProvider(data.captcha_provider)
      setCaptchaSiteKey(data.captcha_site_key || '')
    }).catch(() => {})
  }, [])

  useEffect(() => {
    if (captchaProvider !== 'turnstile' || !captchaSiteKey) return
    if (document.querySelector('script[src*="turnstile"]')) return
    const script = document.createElement('script')
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js'
    script.async = true
    script.defer = true
    script.onload = () => {
      const container = document.getElementById('turnstile-widget')
      if (container && typeof (window as unknown as { turnstile?: { render: (el: string | HTMLElement, o: { sitekey: string }) => string } }).turnstile?.render === 'function') {
        const turnstile = (window as unknown as { turnstile: { render: (el: string | HTMLElement, o: { sitekey: string }) => string } }).turnstile
        turnstileWidgetIdRef.current = turnstile.render(container, { sitekey: captchaSiteKey })
      }
    }
    document.head.appendChild(script)
    return () => {
      if (turnstileWidgetIdRef.current !== null && typeof (window as unknown as { turnstile?: { remove: (id: string) => void } }).turnstile?.remove === 'function') {
        (window as unknown as { turnstile: { remove: (id: string) => void } }).turnstile.remove(turnstileWidgetIdRef.current)
      }
    }
  }, [captchaProvider, captchaSiteKey])

  useEffect(() => {
    if (captchaProvider !== 'recaptcha' || !captchaSiteKey) return
    if (document.querySelector('script[src*="recaptcha"]')) return
    const script = document.createElement('script')
    script.src = `https://www.google.com/recaptcha/api.js?render=${captchaSiteKey}`
    script.async = true
    document.head.appendChild(script)
  }, [captchaProvider, captchaSiteKey])

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const form = e.currentTarget
    const data = new FormData(form)
    const name = (data.get('name') as string) || ''
    const email = (data.get('email') as string) || ''
    const phone = (data.get('phone') as string) || ''
    const company = (data.get('company') as string) || ''
    const message = (data.get('message') as string) || ''
    const website = (data.get('website') as string) || ''

    let turnstileToken: string | undefined
    let recaptchaToken: string | undefined

    if (captchaProvider === 'turnstile' && turnstileWidgetIdRef.current !== null) {
      const turnstile = (window as unknown as { turnstile?: { getResponse: (id: string) => string } }).turnstile
      turnstileToken = turnstile?.getResponse(turnstileWidgetIdRef.current) || undefined
    }
    if (captchaProvider === 'recaptcha' && captchaSiteKey) {
      const g = (window as unknown as { grecaptcha?: { ready: (cb: () => void) => void; execute: (key: string, o: { action: string }) => Promise<string> } }).grecaptcha
      if (g) {
        recaptchaToken = await new Promise<string | undefined>((resolve) => {
          g.ready!(() => {
            g.execute!(captchaSiteKey, { action: 'contact_submit' }).then(resolve).catch(() => resolve(undefined))
          })
        })
      }
    }

    setLoading(true)
    setError(false)
    try {
      await api.post('/api/contact/submit', {
        name,
        email,
        phone: phone || undefined,
        company: company || undefined,
        message: message || undefined,
        sms_consent: smsConsent,
        sms_consent_at: smsConsent ? new Date().toISOString() : undefined,
        website: website || undefined,
        turnstile_token: turnstileToken,
        recaptcha_token: recaptchaToken,
      })
      setSuccess(true)
      if (captchaProvider === 'turnstile' && turnstileWidgetIdRef.current !== null && typeof (window as unknown as { turnstile?: { reset: (id: string) => void } }).turnstile?.reset === 'function') {
        (window as unknown as { turnstile: { reset: (id: string) => void } }).turnstile.reset(turnstileWidgetIdRef.current)
      }
    } catch {
      setError(true)
      if (captchaProvider === 'turnstile' && turnstileWidgetIdRef.current !== null && typeof (window as unknown as { turnstile?: { reset: (id: string) => void } }).turnstile?.reset === 'function') {
        (window as unknown as { turnstile: { reset: (id: string) => void } }).turnstile.reset(turnstileWidgetIdRef.current)
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <PageMeta
        title={pageMeta.contact.title}
        description={pageMeta.contact.description}
        canonical={pageMeta.contact.canonical}
        schema={contactSchema}
      />
      <main className="min-h-screen pt-24 pb-16 px-6">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
          <div>
            <h2 className="font-display font-bold text-2xl uppercase text-white mb-6">
              Send us a message
            </h2>
            {success ? (
              <div className="p-8 rounded-lg" style={{ backgroundColor: 'var(--bg-3)' }}>
                <p className="font-body text-cream-dim mb-6">
                  We'll be in touch within one business day.
                </p>
                <a
                  href="#booking"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-lg font-body font-medium bg-accent text-white"
                >
                  Book a Discovery Call
                </a>
              </div>
            ) : error ? (
              <div className="p-6 rounded-lg" style={{ backgroundColor: 'var(--bg-3)', borderLeft: '4px solid #ef4444' }}>
                <p className="font-body text-cream-dim">
                  Something went wrong. Please call us at 971.238.4801.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Honeypot: hidden from users; bots that fill it get 200 but no lead */}
                <div
                  aria-hidden="true"
                  style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}
                >
                  <label htmlFor="contact-website">Website</label>
                  <input
                    id="contact-website"
                    type="text"
                    name="website"
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label htmlFor="contact-name" className="block font-body text-sm text-cream-dim mb-2">Name *</label>
                  <input
                    id="contact-name"
                    type="text"
                    name="name"
                    required
                    className="w-full px-4 py-3 rounded-lg font-body"
                    style={{ backgroundColor: 'var(--bg-3)', border: '1px solid var(--accent-line)', color: 'var(--white)' }}
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block font-body text-sm text-cream-dim mb-2">Email *</label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 rounded-lg font-body"
                    style={{ backgroundColor: 'var(--bg-3)', border: '1px solid var(--accent-line)', color: 'var(--white)' }}
                  />
                </div>
                <div>
                  <label htmlFor="contact-phone" className="block font-body text-sm text-cream-dim mb-2">Phone</label>
                  <input
                    id="contact-phone"
                    type="tel"
                    name="phone"
                    className="w-full px-4 py-3 rounded-lg font-body"
                    style={{ backgroundColor: 'var(--bg-3)', border: '1px solid var(--accent-line)', color: 'var(--white)' }}
                  />
                  <p className="mt-2 font-body text-sm text-cream-dim">
                    Entering a phone number alone does not opt you in to SMS.
                  </p>
                  <p className="mt-2 font-body text-sm text-cream-dim">
                    <strong className="text-white">Marketing SMS Consent (Optional):</strong>
                  </p>
                  <div className="mt-3 flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="sms_consent"
                      checked={smsConsent}
                      onChange={(e) => setSmsConsent(e.target.checked)}
                      className="mt-1 h-4 w-4 rounded border-accent-line accent-accent"
                    />
                    <label htmlFor="sms_consent" className="font-body text-sm text-cream-dim">
                      I agree to receive marketing and promotional SMS messages from NextTech Consultants at this number (optional).
                    </label>
                  </div>
                  <p className="mt-2 font-body text-xs text-cream-dim leading-relaxed">
                    By checking this box, you provide express written consent to receive marketing and promotional SMS messages from NextTech Consultants. Message frequency may vary. Message and data rates may apply. Text HELP for help. Text STOP to opt out. Consent is not a condition of purchase. See our{' '}
                    <a href="https://nexttechconsultants.com/sms-marketing-terms" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">Terms of Service</a>{' '}
                    and{' '}
                    <a href="https://nexttechconsultants.com/privacy" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">Privacy Policy</a>{' '}
                    (https://nexttechconsultants.com/privacy).
                  </p>
                </div>
                <div>
                  <label htmlFor="contact-company" className="block font-body text-sm text-cream-dim mb-2">Company</label>
                  <input
                    id="contact-company"
                    type="text"
                    name="company"
                    className="w-full px-4 py-3 rounded-lg font-body"
                    style={{ backgroundColor: 'var(--bg-3)', border: '1px solid var(--accent-line)', color: 'var(--white)' }}
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block font-body text-sm text-cream-dim mb-2">Message</label>
                  <textarea
                    id="contact-message"
                    name="message"
                    rows={4}
                    className="w-full px-4 py-3 rounded-lg font-body resize-none"
                    style={{ backgroundColor: 'var(--bg-3)', border: '1px solid var(--accent-line)', color: 'var(--white)' }}
                  />
                </div>
                {captchaProvider === 'turnstile' && (
                  <div className="flex justify-center">
                    <div id="turnstile-widget" />
                  </div>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 rounded-lg font-body font-medium text-white disabled:opacity-50"
                  style={{ backgroundColor: 'var(--accent)' }}
                >
                  {loading ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            )}
          </div>

          <div id="booking">
            <h2 className="font-display font-bold text-2xl uppercase text-white mb-6">
              Or book directly
            </h2>
            <GHLCalendar />
          </div>
        </div>

        <div className="max-w-4xl mx-auto mb-16 p-6 rounded-lg" style={{ backgroundColor: 'var(--bg-3)' }}>
          <h3 className="font-display font-bold text-lg uppercase text-white mb-4">Contact details</h3>
          <p className="font-body text-cream-dim">Phone: 971.238.4801</p>
          <p className="font-body text-cream-dim">Email: info@nexttechconsultants.com</p>
          <p className="font-body text-cream-dim">Serving the Portland Metro &amp; Pacific Northwest</p>
          <p className="font-body text-cream-dim">Hillsboro · Tigard · Vancouver · McMinnville · Newberg</p>
          <p className="font-body text-cream-dim">Hours: Monday–Friday, 8am–6pm PT</p>
        </div>

        <SwitchingFaq />
      </main>
    </>
  )
}
